import React, { useState, useEffect, useMemo } from 'react';
import { UserLayout } from '../../components/user/UserLayout';
import { CheckCircle, Calendar, Filter, Users, Clock, MapPin, Armchair } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card } from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import axios from 'axios';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

export const ProactiveCheckins = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [proactiveCheckins, setProactiveCheckins] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [staffList, setStaffList] = useState([]);
  
  // Filters
  const [fromDate, setFromDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7); // Default to 7 days ago
    return date.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [selectedStaff, setSelectedStaff] = useState('all');
  
  const { toast } = useToast();

  useEffect(() => {
    const userData = localStorage.getItem('userData');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      fetchStaffList(parsedUser.entityId);
      fetchProactiveCheckins(parsedUser.entityId);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchProactiveCheckins(user.entityId);
    }
  }, [fromDate, toDate, selectedStaff]);

  const fetchStaffList = async (propertyId) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/staff/by-role/${propertyId}/Pool and Beach Server`
      );
      if (response.data.success) {
        setStaffList(response.data.staff);
      }
    } catch (error) {
      console.error('Error fetching staff list:', error);
    }
  };

  const fetchProactiveCheckins = async (propertyId) => {
    setLoading(true);
    try {
      let url = `${BACKEND_URL}/api/reports/proactive-checkins/${propertyId}?from_date=${fromDate}&to_date=${toDate}`;
      if (selectedStaff && selectedStaff !== 'all') {
        url += `&staff_id=${selectedStaff}`;
      }
      
      const response = await axios.get(url);
      if (response.data.success) {
        setProactiveCheckins(response.data.proactiveCheckins);
        setTotalCount(response.data.totalCount);
      }
    } catch (error) {
      console.error('Error fetching proactive checkins:', error);
      toast({
        title: "Error",
        description: "Failed to load proactive checkins data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    return `${seconds.toFixed(1)}s`;
  };

  return (
    <UserLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Proactive Checkins</h1>
            <p className="text-slate-600 mt-1">Monitor proactive guest check-ins (calls under 5 seconds)</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-slate-500" />
            <h2 className="font-semibold text-slate-800">Filters</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* From Date */}
            <div className="space-y-2">
              <Label htmlFor="fromDate" className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-500" />
                From Date
              </Label>
              <Input
                id="fromDate"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            
            {/* To Date */}
            <div className="space-y-2">
              <Label htmlFor="toDate" className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-500" />
                To Date
              </Label>
              <Input
                id="toDate"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
            
            {/* Staff Filter */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Users className="w-4 h-4 text-slate-500" />
                Pool & Beach Server
              </Label>
              <Select value={selectedStaff} onValueChange={setSelectedStaff}>
                <SelectTrigger>
                  <SelectValue placeholder="All Staff" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Staff</SelectItem>
                  {staffList.map((staff) => (
                    <SelectItem key={staff.id} value={staff.id}>
                      {staff.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Summary */}
            <div className="flex items-end">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 w-full">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="text-xs text-green-600 font-medium">Total Proactive Checkins</p>
                    <p className="text-2xl font-bold text-green-700">{totalCount}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Results Table */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-slate-600">Loading proactive checkins...</p>
            </div>
          ) : proactiveCheckins.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">No Proactive Checkins Found</h3>
              <p className="text-slate-600">
                No proactive checkins recorded for the selected date range and filters.
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">#</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Date & Time</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Room</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Guest</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Seats</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Duration</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Staff</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Type</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {proactiveCheckins.map((checkin, index) => (
                      <tr key={checkin.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4 text-sm text-slate-500">{index + 1}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-slate-400" />
                            <span className="text-sm text-slate-900">{formatDateTime(checkin.startTime)}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-blue-500" />
                            <span className="text-sm font-semibold text-slate-900">{checkin.roomNumber}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-900">{checkin.guestName}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-1 flex-wrap">
                            {checkin.seatNames?.length > 0 ? (
                              checkin.seatNames.map((seat, idx) => (
                                <span key={idx} className="inline-flex items-center px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-xs">
                                  <Armchair className="w-3 h-3 mr-1" />
                                  {seat}
                                </span>
                              ))
                            ) : (
                              <span className="text-slate-400 text-sm">-</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                            {formatDuration(checkin.durationSeconds)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-1">
                            {checkin.staffNames?.length > 0 ? (
                              checkin.staffNames.map((name, idx) => (
                                <span key={idx} className="inline-flex items-center px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                                  {name}
                                </span>
                              ))
                            ) : (
                              <span className="text-slate-400 text-sm">-</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {checkin.callingMode === 'Calling for Checkout' ? (
                            <span className="inline-flex items-center px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">
                              Checkout
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 bg-teal-100 text-teal-700 rounded-full text-xs font-semibold">
                              Regular
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Footer */}
              <div className="bg-slate-50 px-6 py-4 border-t border-slate-200">
                <p className="text-sm text-slate-600">
                  Showing <strong>{proactiveCheckins.length}</strong> proactive checkin{proactiveCheckins.length !== 1 ? 's' : ''}
                </p>
              </div>
            </>
          )}
        </div>

        {/* Charts Section */}
        {proactiveCheckins.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Daily Trend Chart */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Daily Proactive Checkins Trend</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart
                  data={(() => {
                    const dailyData = {};
                    proactiveCheckins.forEach(c => {
                      const date = new Date(c.startTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                      dailyData[date] = (dailyData[date] || 0) + 1;
                    });
                    return Object.entries(dailyData).map(([date, count]) => ({ date, count }));
                  })()}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="count" name="Proactive Checkins" stroke="#22c55e" strokeWidth={2} dot={{ fill: '#22c55e' }} />
                </LineChart>
              </ResponsiveContainer>
            </Card>

            {/* By Staff Bar Chart */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Proactive Checkins by Staff</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={(() => {
                    const staffData = {};
                    proactiveCheckins.forEach(c => {
                      (c.staffNames || []).forEach(name => {
                        staffData[name] = (staffData[name] || 0) + 1;
                      });
                    });
                    return Object.entries(staffData)
                      .map(([name, count]) => ({ name, count }))
                      .sort((a, b) => b.count - a.count)
                      .slice(0, 10);
                  })()}
                  margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 10, angle: -45, textAnchor: 'end' }} interval={0} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" name="Checkins" fill="#22c55e" />
                </BarChart>
              </ResponsiveContainer>
            </Card>

            {/* By Type Pie Chart */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Checkin Type Distribution</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={(() => {
                      const regular = proactiveCheckins.filter(c => c.callingMode !== 'Calling for Checkout').length;
                      const checkout = proactiveCheckins.filter(c => c.callingMode === 'Calling for Checkout').length;
                      return [
                        { name: 'Regular', value: regular },
                        { name: 'Checkout', value: checkout },
                      ];
                    })()}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    dataKey="value"
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  >
                    <Cell fill="#14b8a6" />
                    <Cell fill="#a855f7" />
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Card>

            {/* Hourly Distribution */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Hourly Distribution</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={(() => {
                    const hourlyData = {};
                    for (let i = 0; i < 24; i++) hourlyData[i] = 0;
                    proactiveCheckins.forEach(c => {
                      const hour = new Date(c.startTime).getHours();
                      hourlyData[hour] = (hourlyData[hour] || 0) + 1;
                    });
                    return Object.entries(hourlyData).map(([hour, count]) => ({ 
                      hour: `${hour}:00`, 
                      count 
                    }));
                  })()}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" tick={{ fontSize: 10 }} interval={2} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" name="Checkins" fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>
        )}
      </div>
    </UserLayout>
  );
};
